'use client';
import { useEffect, useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import UserEditModal from '../components/UserEditModal';

interface User {
  id: number;
  name: string;
  telegram_id: number;
  role: string;
  timezone?: string;
}

function LayoutContent({ children }: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const uid = searchParams.get('uid');
    
    if (uid && uid !== 'unknown') {
      fetch(`/api/users/me?uid=${uid}`)
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            setUser(data.user);
          }
          setLoading(false);
        })
        .catch(error => {
          console.error('Failed to fetch user:', error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const handleSaveName = async (userData: { name: string; timezone: string }) => {
    if (!user) return;

    try {
      const response = await fetch('/api/users/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          newName: userData.name,
          timezone: userData.timezone
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setUser({ ...user, name: userData.name, timezone: userData.timezone });
        setIsModalOpen(false);
      } else {
        console.error('Failed to update name:', data.error);
      }
    } catch (error) {
      console.error('Error updating name:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg"></div>
              <span className="text-xl font-semibold">Lanloop</span>
            </div>
            <nav className="flex space-x-6">
              {!loading && user && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center space-x-1 text-gray-600 hover:text-blue-600 font-medium transition"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>{user.name}</span>
                </button>
              )}
              {/* <a href="/account" className="text-blue-600 font-medium">Home</a> */}
              {/* <a href="/account/student" className="text-gray-600 hover:text-blue-600">Student</a> */}
              {/* <a href="/account/teacher" className="text-gray-600 hover:text-blue-600">Teacher</a> */}
              {/* <a href="/account/admin" className="text-gray-600 hover:text-blue-600">Admin</a> */}
              <button 
                onClick={() => {
                  const currentUrl = new URL(window.location.href);
                  const currentUid = currentUrl.searchParams.get('uid');

                  const browserUrl = currentUid
                      ? `https://meet.lanloop.site${currentUrl.pathname}?uid=${currentUid}&auth=1`
                      : `https://meet.lanloop.site${currentUrl.pathname}`;
                  window.open(browserUrl, '_blank');
                }}
                className="px-3 py-1 bg-yellow-400 text-white rounded-lg hover:bg-yellow-500 transition-colors"
              >
                Browser
              </button>
            </nav>
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8 flex-1 overflow-auto [scrollbar-width:none] [-ms-overflow-style:none]">
        {children}
      </main>

      {user && (
        <UserEditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveName}
          currentName={user.name}
          currentTimezone={user.timezone}
          userId={user.id}
        />
      )}
    </div>
  );
}

export default function AccountLayout({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-white text-gray-900">
        <header className="border-b border-gray-200 bg-white">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg"></div>
                <span className="text-xl font-semibold">Lanloop</span>
              </div>
              <nav className="flex items-center space-x-6">
                <div className="text-gray-600">Загрузка...</div>
              </nav>
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-8 flex-1 overflow-auto">
          {children}
        </main>
      </div>
    }>
      <LayoutContent>
        {children}
      </LayoutContent>
    </Suspense>
  );
}
